import {
  ClientSideIntegration,
  IntegrationDataFlow,
  IntegrationType,
} from 'studio5/pages/integrations/integrations.types';

export const AMPLITUDE_ID = 'amplitude';
export const CUSTOMER_IO_ID = 'customerio';
export const GOOGLE_ANALYTICS_ID = 'ga';
export const GOOGLE_TAG_MANAGER_ID = 'gtm';
export const HEAP_ID = 'heap';
export const HOTJAR_ID = 'hotjar';
export const INTERCOM_ID = 'intercom';
export const KISSMETRICS_ID = 'kissmetrics';
export const KLAVIYO_ID = 'klaviyo';
export const LOCALYTICS_ID = 'localytics';
export const LOGROCKET_ID = 'logrocket';
export const MIXPANEL_ID = 'mixpanel';
export const RUDDERSTACK_ID = 'rudderstack';
export const SEGMENT_ID = 'segment';
export const VERO_ID = 'vero';
export const WOOPRA_ID = 'woopra';

export type IntegrationClientSideId =
  | typeof AMPLITUDE_ID
  | typeof CUSTOMER_IO_ID
  | typeof GOOGLE_ANALYTICS_ID
  | typeof GOOGLE_TAG_MANAGER_ID
  | typeof HEAP_ID
  | typeof HOTJAR_ID
  | typeof INTERCOM_ID
  | typeof KISSMETRICS_ID
  | typeof KLAVIYO_ID
  | typeof LOCALYTICS_ID
  | typeof LOGROCKET_ID
  | typeof MIXPANEL_ID
  | typeof RUDDERSTACK_ID
  | typeof SEGMENT_ID
  | typeof VERO_ID
  | typeof WOOPRA_ID;

export const INTEGRATIONS_CLIENT_SIDE: Record<
  IntegrationClientSideId,
  ClientSideIntegration
> = {
  [AMPLITUDE_ID]: {
    id: AMPLITUDE_ID,
    name: 'Amplitude',
    logoAssetUrl: new URL('assets/amplitude.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/amplitude-two-way-integration',
    isPremium: false,
    integrationType: IntegrationType.APPCUES_JS_SDK,
    dataFlowDirection: IntegrationDataFlow.TWO_WAY,
    shortDescription:
      'Pass data and sync segments to target users with relevant in-product experiences and deeply analyze the results alongside the rest of your product data.',
    longDescription: `
    Appcues’ Amplitude integration makes it easy to measure and understand the impact that Appcues experiences have on activation, engagement, and retention. You can also sync Amplitude segments to automate more sophisticated and personalized flows.

    - Measure the impact of Appcues experiences by gaining deeper insights and creating funnels
    - Utilize segment data in Amplitude to recommend features to your users based on past behaviors, such as the usage of similar features
    - Analyze how Appcues flows impact key behaviors by investigating drop-off points and understanding abandoned user segments`,
  },
  [CUSTOMER_IO_ID]: {
    id: CUSTOMER_IO_ID,
    name: 'Customer.io',
    logoAssetUrl: new URL('assets/customer-io.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/customerio-integration',
    isPremium: false,
    integrationType: IntegrationType.APPCUES_JS_SDK,
    dataFlowDirection: IntegrationDataFlow.OUTBOUND,
    shortDescription:
      'Create an omnichannel marketing strategy by targeting campaigns based on user interaction.',
    longDescription: `
    Appcues' Customer.io Integration makes it easy to automatically target marketing campaigns based on user's interactions.

    - Send emails and other communications to users when they interact with Appcues experiences (user completes or skips  flows, checklists, etc.)
    - Follow up with users via email after they provide an in-product NPS score
    - Create a multi-channel onboarding experience with more relevant messaging by triggering emails to users who have taken/failed to take key steps in the product`,
  },
  [GOOGLE_ANALYTICS_ID]: {
    id: GOOGLE_ANALYTICS_ID,
    name: 'Google Analytics',
    logoAssetUrl: new URL('assets/ga.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/google-analytics-integration',
    isPremium: false,
    integrationType: IntegrationType.APPCUES_JS_SDK,
    dataFlowDirection: IntegrationDataFlow.OUTBOUND,
    shortDescription:
      'Pass event data for deeper insights and optimized experiences.',
    longDescription: `
    Appcues' Google Analytics Integration makes it easy to visualize acquisition channels and identify trends in user interaction with in-product messaging.

    - Discover how different acquisition channels affect user interactions with your flows
    - Discover how users find your product, interact with in-product content, and what features bring them the most value
    - Identify where your most engaged and active users come from so that you can focus your marketing efforts
  `,
  },
  [GOOGLE_TAG_MANAGER_ID]: {
    id: GOOGLE_TAG_MANAGER_ID,
    name: 'Google Tag Manager',
    logoAssetUrl: new URL('assets/gtm.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/integration-documents/google-tag-manager-integration?from_search=142205068',
    isPremium: false,
    integrationType: IntegrationType.APPCUES_JS_SDK,
    dataFlowDirection: IntegrationDataFlow.OUTBOUND,
    shortDescription:
      "Pass event data when user's interact with Appcues experiences.",
    longDescription: `
    Appcues' Google Tag Manager Integration makes it easy to automatically pass events when users interact with in-product experiences.

    - Trigger tags, events, and more when users interact with Appcues experiences (like completing, skipping, or interacting with a flow, checklist, etc.)
    - Trigger tags, events, and more when a user provides an NPS score and feedback
  `,
  },
  [HEAP_ID]: {
    id: HEAP_ID,
    name: 'Heap',
    logoAssetUrl: new URL('assets/heap.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/heap-two-way-integration',
    isPremium: false,
    integrationType: IntegrationType.APPCUES_JS_SDK,
    dataFlowDirection: IntegrationDataFlow.TWO_WAY,
    shortDescription:
      'Pass data and sync segments to target users with relevant in-product experiences and deeply analyze the results with the rest of your product data.',
    longDescription: `
    Appcues’ Heap integration makes it easy to measure and understand the impact that Appcues experiences have on activation, engagement, and retention. You can also sync Heap behavioral segments to automate more sophisticated and personalized flows.

    - Create detailed reports for individual Appcues experiences to gain deeper insights
    - Utilize segment data in Heap to recommend features to your users based on past behaviors, such as the usage of similar features
    - Analyze how Appcues flows impact key behaviors by investigating drop-off points and understanding abandoned user segments
    - Improve experiences by identifying which flows are correlated with a higher retention rate
  `,
  },
  [HOTJAR_ID]: {
    id: HOTJAR_ID,
    name: 'Hotjar',
    rolloutFeatureFlag: 'HOTJAR_INTEGRATION',
    logoAssetUrl: new URL('assets/hotjar.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/integration-documents/hotjar-integration?from_search=123424621',
    isPremium: false,
    integrationType: IntegrationType.APPCUES_JS_SDK,
    dataFlowDirection: IntegrationDataFlow.OUTBOUND,
    shortDescription:
      'Watch users interact with in-product experiences for a more complete picture of user behavior. ',
    longDescription: `
  Appcues' Hotjar integration makes it easy to learn more about users by watching them interact with your product and in-product experiences.

    Pass both flow interaction and click-to-track events from Appcues, making it easier to get deeper insights in a matter of minutes.
    - Watch users interact with flows to ensure they are working as intended, the copy is clear, the content displays as desired, etc.
    - Identify users who offer a negative NPS score, then watch their session to understand their in-product interactions and frustration better
    - Track users who fail to complete core flows, then watch their session to understand why
    - Measure engagement with a beta or new product, then discover how users are exploring the feature and what additional in-app guidance might be needed
    - Spot user issues, confusion, or points of friction in a session and quickly create or adjust your Appcues flows to limit those problems
  `,
  },
  [INTERCOM_ID]: {
    id: INTERCOM_ID,
    name: 'Intercom',
    logoAssetUrl: new URL('assets/intercom.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/intercom-integration',
    isPremium: false,
    integrationType: IntegrationType.APPCUES_JS_SDK,
    dataFlowDirection: IntegrationDataFlow.OUTBOUND,
    shortDescription:
      'Pass event data to enable customer support teams to take action based on user interactions with in-product experiences.',
    longDescription: `
    Appcues' Intercom Integration makes it easy to see how users are interacting with in-product experiences, so they can better support those users and increase satisfaction.

    - Understand which users are completing and skipping in-product flows, checklists, and more to help your teams provide more valuable support and follow-up communication
    - Pass NPS scores and feedback to Intercom for a more complete view of customer satisfaction
  `,
  },
  [KISSMETRICS_ID]: {
    id: KISSMETRICS_ID,
    name: 'Kissmetrics',
    logoAssetUrl: new URL('assets/kissmetrics.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/kissmetrics-integration',
    isPremium: false,
    integrationType: IntegrationType.APPCUES_JS_SDK,
    dataFlowDirection: IntegrationDataFlow.OUTBOUND,
    shortDescription:
      'Pass event data for deeper insights and optimized experiences.',
    longDescription: `
    Appcues’ Kissmetrics Integration makes it easy to measure and understand the impact that Appcues experiences have on activation, engagement, and retention.

    - Create detailed reports for individual Appcues experiences to track performance and gain deeper insights
    - Build funnels to help you test and adjust your flows for the best impact
  `,
  },
  [KLAVIYO_ID]: {
    id: KLAVIYO_ID,
    name: 'Klaviyo',
    logoAssetUrl: new URL('assets/klaviyo.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/klaviyo-integration',
    isPremium: false,
    integrationType: IntegrationType.APPCUES_JS_SDK,
    dataFlowDirection: IntegrationDataFlow.OUTBOUND,
    shortDescription:
      'Create an omnichannel marketing strategy by targeting campaigns based on user interaction. ',
    longDescription: `
    Appcues' Klaviyo Integration makes it easy to automatically target marketing campaigns based on user's interactions.

    - Send emails and other communications to users when they interact with Appcues experiences (user completes or skips  flows, checklists, etc.)
    - Follow up with users via email after they provide an in-product NPS score
    - Create a multi-channel onboarding experience with more relevant messaging by triggering emails to users who have taken/failed to take key steps in the product
  `,
  },
  [LOCALYTICS_ID]: {
    id: LOCALYTICS_ID,
    name: 'Localytics',
    logoAssetUrl: new URL('assets/localytics.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/localytics-integration',
    isPremium: false,
    integrationType: IntegrationType.APPCUES_JS_SDK,
    dataFlowDirection: IntegrationDataFlow.OUTBOUND,
    shortDescription:
      'Pass event data for deeper insights and optimized experiences.',
    longDescription: `
    Appcues' Localytics Integration makes it easy to enable communication across devices.

    - Send a push notification after a user completes, skips, or interacts with an in-product experience (flows, checklists, etc.)
    - Ask users if they want to try out an app feature, then notify them on their phone with a deep link to it
  `,
  },
  [LOGROCKET_ID]: {
    id: LOGROCKET_ID,
    name: 'LogRocket',
    rolloutFeatureFlag: 'LOGROCKET_INTEGRATION',
    logoAssetUrl: new URL('assets/logrocket.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/integration-documents/logrocket-integration',
    isPremium: false,
    integrationType: IntegrationType.APPCUES_JS_SDK,
    dataFlowDirection: IntegrationDataFlow.OUTBOUND,
    shortDescription:
      'Watch users interact with in-product experiences for a more complete picture of user behavior. ',
    longDescription: `
    Appcues' LogRocket integration makes it easy to learn more about users by watching them interact with your product and in-product experiences.

    Pass both flow interaction and click-to-track events from Appcues, making it easier to get deeper insights in a matter of minutes.
    - Watch users interact with flows to ensure they are working as intended, the copy is clear, the content displays as desired, etc.
    - Identify users who offer a negative NPS score, then watch their session to understand their in-product interactions and frustration better
    - Track users who fail to complete core flows, then watch their session to understand why
    - Measure engagement with a beta or new product, then discover how users are exploring the feature and what additional in-app guidance might be needed
    - Spot user issues, confusion, or points of friction in a session and quickly create or adjust your Appcues flows to limit those problems
  `,
  },
  [MIXPANEL_ID]: {
    id: MIXPANEL_ID,
    name: 'Mixpanel',
    logoAssetUrl: new URL('assets/mixpanel.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/mixpanel-two-way-integration',
    isPremium: false,
    integrationType: IntegrationType.APPCUES_JS_SDK,
    dataFlowDirection: IntegrationDataFlow.TWO_WAY,
    shortDescription:
      'Pass data and sync segments to target users with relevant in-product experiences and deeply analyze the results with the rest of your product data.',
    longDescription: `
    Appcues’ Mixpanel Integration makes it easy to measure and understand the impact that Appcues experiences have on activation, engagement, and retention. You can also sync Mixpanel cohorts to automate more sophisticated and personalized flows.

    - Create detailed reports for individual Appcues experiences to track performance and gain deeper insights
    - Improve experiences by identifying which flows are correlated with a higher retention rate
    - Create onboarding, activation, and retention funnels in Mixpanel to help you test and adjust your flows for these metrics
    - Create cohorts based on specific behaviors and attributes and pass them to Appcues to target them with flows (such as recommending new features to users who've interacted with similar features)
  `,
  },
  [RUDDERSTACK_ID]: {
    id: RUDDERSTACK_ID,
    name: 'RudderStack',
    rolloutFeatureFlag: 'RUDDERSTACK_INTEGRATION',
    logoAssetUrl: new URL('assets/rudderstack.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/rudderstack-integration, Data into Appcues',
    isPremium: false,
    integrationType: IntegrationType.APPCUES_JS_SDK,
    dataFlowDirection: IntegrationDataFlow.OUTBOUND,
    shortDescription:
      'Enable the collection and integration of user data across all of your specialized tools.',
    longDescription: `
    Appcues' rudderstack integration makes it easy to automatically collect user data and pass it to other specialized tools, so your team can make the most of the information you all share.

    - Collect data when users take key actions (which users have/haven't seen, completed, and interacted with flows) and pass them to analytics tools (like Heap, Mixpanel, Amplitude, and Google Analytics) for deeper analysis and insights
    - Continue your user's experience across multiple tools (like your marketing automation platform) to send messages after users take action
    - Give users access to features within your product management tool of choice and pass those properties to Appcues to target those users with in-app experiences describing how to use the feature
  `,
  },
  [SEGMENT_ID]: {
    id: SEGMENT_ID,
    name: 'Segment',
    logoAssetUrl: new URL('assets/segment.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/segment-two-way-integration',
    isPremium: false,
    integrationType: IntegrationType.APPCUES_JS_SDK,
    dataFlowDirection: IntegrationDataFlow.TWO_WAY,
    shortDescription:
      'Enable the collection and integration of user data across all of your specialized tools.',
    longDescription: `
    Appcues' Segment integration makes it easy to automatically collect user data and pass it to other specialized tools, so your team can make the most of the information you all share.

    - Collect data when users take key actions (which users have/haven't seen, completed, and interacted with flows) and pass them to analytics tools (like Heap, Mixpanel, Amplitude, and Google Analytics) for deeper analysis and insights
    - Continue your user's experience across multiple tools (like your marketing automation platform) to send messages after users take action
    - Give users access to features within your product management tool of choice and pass those properties to Appcues to target those users with in-app experiences describing how to use the feature
  `,
  },
  [VERO_ID]: {
    id: VERO_ID,
    name: 'Vero',
    logoAssetUrl: new URL('assets/vero.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/vero-integration',
    isPremium: false,
    integrationType: IntegrationType.APPCUES_JS_SDK,
    dataFlowDirection: IntegrationDataFlow.OUTBOUND,
    shortDescription:
      'Create an omnichannel marketing strategy by targeting campaigns based on user interaction. ',
    longDescription: `
    Appcues' Vero Integration makes it easy to automatically target marketing campaigns based on user's interactions.

    - Send emails and other communications to users when they interact with Appcues experiences (user completes or skips  flows, checklists, etc.)
    - Follow up with users via email after they provide an in-product NPS score
    - Create a multi-channel onboarding experience with more relevant messaging by triggering emails to users who have taken/failed to take key steps in the product
  `,
  },
  [WOOPRA_ID]: {
    id: WOOPRA_ID,
    name: 'Woopra',
    logoAssetUrl: new URL('assets/woopra.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/woopra-integration',
    isPremium: false,
    integrationType: IntegrationType.APPCUES_JS_SDK,
    dataFlowDirection: IntegrationDataFlow.OUTBOUND,
    shortDescription:
      'Pass event data for deeper insights and trigger emails after user interactions.',
    longDescription: `
    Appcues Woopra Integration makes it easy to visualize and analyze the entire customer journey from the first marketing touch to product usage.

    - Send emails after a user completes, skips, or interacts with an in-product experience (flows, checklists, etc.)
    - Send a follow-up email after a user submits an NPS score and feedback
  `,
  },
};
